import {
  Link,
  spacingHelper,
  typographyHelper,
  paletteHelper,
} from '@rea-group/construct-kit-core';
import { ReactElement } from 'react';
import styled from 'styled-components';

type NavigationLinksProps = {
  currentPage: string;
  isToggled: boolean;
  trackLinkClickThrough: (element: string) => void;
};

const AGENT_PATTERN = /^\/(find-agent|find-agency|agency|agent)/gi;

const CLICK_THROUGH_ELEMENT_PREFIX = 'header';

const NavLink = styled(Link).attrs({ variant: 'secondary' })`
  ${typographyHelper('subtitle01')};
  text-decoration: none;
  padding: ${spacingHelper('medium')};
  white-space: nowrap;
  text-align: left;

  ${({ theme }) => theme.layoutBreakpoints.extraLarge} {
    text-align: center;
    padding: ${spacingHelper('small')};
    align-self: center;
  }

  &[aria-current='page'] {
    box-shadow: inset ${spacingHelper('twoExtraSmall')} 0px
      ${paletteHelper('graphicBlue')};
    color: ${paletteHelper('textPrimary')};

    ${({ theme }) => theme.layoutBreakpoints.extraLarge} {
      box-shadow: 0px 0.125rem ${paletteHelper('graphicBlue')};
    }
  }

  &:hover {
    text-decoration: none;
  }
`;

const ariaCurrentValue = (isCurrentPage: boolean): 'false' | 'page' =>
  isCurrentPage ? 'page' : 'false';

interface NavLinkContainerProps {
  $isToggled: boolean;
}

const NavLinkContainer = styled.nav<NavLinkContainerProps>`
  position: fixed;
  top: 3.5rem;
  left: 0;
  display: flex;
  flex-direction: column;
  background-color: ${paletteHelper('backgroundPrimary')};
  height: 100vh;
  bottom: 0;
  max-width: 15rem;
  width: 100%;
  padding-top: ${spacingHelper('large')};
  transform: ${(props) =>
    props.$isToggled ? `translateX(0)` : `translateX(-100%)`};
  transition: transform 0.3s ease-in-out;

  ${({ theme }) => theme.layoutBreakpoints.extraLarge} {
    position: static;
    display: flex;
    flex-direction: row;
    top: 4rem;
    height: auto;
    max-width: none;
    min-width: none;
    padding-top: 0;
    transform: none;
  }
`;

const NavigationLinks = ({
  currentPage,
  isToggled,
  trackLinkClickThrough,
}: NavigationLinksProps): ReactElement => {
  const isPathMatch = (linkPath: string): boolean =>
    currentPage.includes(linkPath);

  const isCurrentLink = (linkPath: string): boolean =>
    linkPath === currentPage || isPathMatch(linkPath);

  const isAgentLink = !!currentPage.match(AGENT_PATTERN);

  const isCurrentPageFromHomepage = /^\/\?.*/;

  const isHighlightFindProperty = (): boolean => {
    if (currentPage === '/' || isCurrentPageFromHomepage.test(currentPage)) {
      return true;
    }
    return ['for-sale', 'for-lease', 'sold', 'leased'].some((channel) =>
      currentPage.split('/').filter(Boolean)[0]?.includes(channel),
    );
  };

  return (
    <NavLinkContainer aria-label="Site navigation" $isToggled={isToggled}>
      <NavLink
        href="/"
        aria-current={ariaCurrentValue(isHighlightFindProperty())}
        aria-label="Go to homepage"
        onClick={() =>
          trackLinkClickThrough(`${CLICK_THROUGH_ELEMENT_PREFIX}:homepage`)
        }
      >
        Find property
      </NavLink>
      <NavLink
        href="/invest/"
        aria-current={ariaCurrentValue(isCurrentLink('/invest/'))}
        aria-label="Go to invest"
        onClick={() =>
          trackLinkClickThrough(`${CLICK_THROUGH_ELEMENT_PREFIX}:invest`)
        }
      >
        Invest
      </NavLink>
      <NavLink
        href="/short-term-workspace/"
        aria-current="false"
        aria-label="Go to short term workspaces"
        onClick={() =>
          trackLinkClickThrough(
            `${CLICK_THROUGH_ELEMENT_PREFIX}:short-term-workspace`,
          )
        }
      >
        Short-term
      </NavLink>
      <NavLink
        href="/find-agent/"
        aria-current={ariaCurrentValue(isAgentLink)}
        aria-label="Go to find agencies"
        onClick={() =>
          trackLinkClickThrough(`${CLICK_THROUGH_ELEMENT_PREFIX}:find-agent`)
        }
      >
        Find agencies
      </NavLink>
      <NavLink
        href="/news/"
        aria-current={ariaCurrentValue(isCurrentLink('/news/'))}
        aria-label="Go to news"
        onClick={() =>
          trackLinkClickThrough(`${CLICK_THROUGH_ELEMENT_PREFIX}:news`)
        }
      >
        News
      </NavLink>
    </NavLinkContainer>
  );
};

export default NavigationLinks;
